import classNames from "classnames";
import {
  findIndex,
  isEmpty,
  isEqual,
  last,
  map,
  pullAllBy,
  pullAt,
} from "lodash";
import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from "uuid";

import { Item, MoreItem, TreeItem } from "../../../@types/appNavigation";
import {
  useAppNavigationItems,
  useUpdateAppNavigation,
} from "../../../hooks/administration/appNavigation";
import PageTitle from "../../layout/PageTitle";
import "../navigationManager.css";
import ItemButton from "../navigationManager/ItemButton";
import ItemForm from "./ItemForm";
import NodeTitle from "./NodeTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { faPlus, faBars } from "@fortawesome/pro-solid-svg-icons";

// Helper functions to replace react-sortable-tree utilities
function changeNodeAtPath({
  treeData,
  path,
  newNode,
  getNodeKey,
}: {
  treeData: (TreeItem | Item)[];
  path: (string | number)[];
  newNode: TreeItem;
  getNodeKey: ({ node }: { node: TreeItem | Item }) => string | number;
}): (TreeItem | Item)[] {
  const nodeId = path[0];
  return treeData.map((node) => {
    if (getNodeKey({ node }) === nodeId) {
      return { ...node, ...newNode };
    }
    return node;
  });
}

function getNodeAtPath({
  treeData,
  path,
  getNodeKey,
}: {
  treeData: (TreeItem | Item)[];
  path: (string | number)[];
  getNodeKey: ({ node }: { node: TreeItem | Item }) => string | number;
}): { node: TreeItem | Item; treeIndex: number } | null {
  const nodeId = path[0];
  const treeIndex = treeData.findIndex(
    (node) => getNodeKey({ node }) === nodeId,
  );
  if (treeIndex === -1) return null;
  return { node: treeData[treeIndex], treeIndex };
}

function removeNodeAtPath({
  treeData,
  path,
  getNodeKey,
}: {
  treeData: (TreeItem | Item)[];
  path: (string | number)[];
  getNodeKey: ({ node }: { node: TreeItem | Item }) => string | number;
}): (TreeItem | Item)[] {
  const nodeId = path[0];
  return treeData.filter((node) => getNodeKey({ node }) !== nodeId);
}

// Sortable Item Component
function SortableItem({
  node,
  onClick,
  buttons,
  className,
  isSelected,
}: {
  node: TreeItem | Item;
  onClick: () => void;
  buttons: JSX.Element[];
  className: string;
  isSelected?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: node.id,
    disabled: node.type === "more",
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const baseClasses = "flex items-center border rounded w-5/6 ";
  const stateClasses = isSelected
    ? "bg-gray-100 border-gray-300"
    : className.includes("border-red-300")
      ? "bg-red-50 border-red-300"
      : "bg-white border-gray-200";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="mb-2.5 mr-2.5"
      {...attributes}
    >
      <div className={`${baseClasses} ${stateClasses}`}>
        {node.type !== "more" && (
          <div
            className="cursor-grab bg-gray-300 px-3 py-3 text-gray-600 flex items-center text-xs border-r border-gray-200"
            {...listeners}
          >
            <FontAwesomeIcon className="h-4 text-white" icon={faBars} />
          </div>
        )}
        <div
          className="flex-1 cursor-pointer px-3 font-bold py-2 flex items-center overflow-hidden"
          onClick={onClick}
        >
          <NodeTitle node={node} />
        </div>
        <div className="flex gap-1 px-2">{buttons}</div>
      </div>
    </div>
  );
}

function AppNavigationManager() {
  const { data, isLoading } = useAppNavigationItems();

  const [more, setMore] = useState<MoreItem>({
    id: "more",
    label: "More",
    type: "more",
    icon: "ellipsis-h",
    isDefault: true,
    isEntryPoint: false,
    options: { items: [] },
  });
  const [treeData, setTreeData] = useState<(TreeItem | Item)[]>([]);
  const [activePath, setActivePath] = useState<(number | string)[]>([]);
  const [treeKey, setTreeKey] = useState(0);
  const { mutate: updateItems, isLoading: isSaving } = useUpdateAppNavigation();

  // Set up sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = treeData.findIndex((item) => item.id === active.id);
      const newIndex = treeData.findIndex((item) => item.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newTreeData = arrayMove(treeData, oldIndex, newIndex);
        setTreeData(newTreeData);
        setActivePath([active.id]);
      }
    }
  }

  useEffect(() => {
    if (!isEmpty(treeData)) return;
    if (!data || isEmpty(data)) return;

    setTreeData(data);

    const moreCandidate = last(data);
    if (moreCandidate?.type == "more") setMore(moreCandidate);
  }, [data]);

  function removeItemAt(
    _e: unknown,
    path: (string | number)[],
    node: TreeItem | Item,
  ) {
    setActivePath([]);
    const newTreeData = removeNodeAtPath({
      treeData: treeData,
      path,
      getNodeKey,
    });

    if (node.isEntryPoint) {
      changeEntryPoint(node, false, newTreeData);
    } else {
      setTreeData(newTreeData);
    }

    moveItemToMore(node);
  }

  function changeActiveNode(newNode: TreeItem) {
    const activeNode = getActiveNode();

    if (activeNode?.id !== newNode.id) {
      setActivePath([newNode.id]);

      if (newNode.isDefault) {
        removeItemFromMore(newNode);
      }

      if (activeNode?.isDefault) {
        moveItemToMore(activeNode);
      }
    }

    const newTreeData = changeNodeAtPath({
      treeData,
      path: activePath,
      getNodeKey,
      newNode,
    });

    if (activeNode?.isEntryPoint && newNode.type === "link") {
      changeEntryPoint(activeNode, false, newTreeData);
    } else {
      // set entry point if none of the other items is
      if (
        findIndex(
          newTreeData,
          (data) => data.isEntryPoint && data.id !== newNode.id,
        ) === -1
      ) {
        changeEntryPoint(newNode, true, newTreeData);
      } else {
        setTreeData(newTreeData);
      }
    }

    if (activeNode?.icon !== newNode.icon) {
      setTreeKey((prev) => prev + 1);
    }
  }

  function moveItemToMore(item: TreeItem | Item) {
    if (item?.isDefault) {
      const newMore = more;
      newMore.options.items.push({ ...item, isEntryPoint: false });

      setMore(newMore);
    }
  }

  function removeItemFromMore(item: TreeItem | Item) {
    const newMore = more;

    pullAllBy(newMore.options.items, [{ id: item.id }], "id");

    setMore(newMore);
  }

  function addItem() {
    const data = treeData;
    const more = pullAt(data, [treeData.length - 1]);

    const newItem = {
      id: uuidv4(),
      label: I18n.t("js.administration.app_navigation_items.edit.new_link"),
      type: "link",
      icon: "link",
      isEntryPoint: false,
      isDefault: false,
      options: { url: "" },
    };

    setTreeData([...data, newItem, ...more]);

    const path = [newItem.id];
    setActivePath(path);
  }

  function itemButtons(node: TreeItem | Item, path: (number | string)[]) {
    const buttons: JSX.Element[] = [];

    if (node.type !== "more") {
      buttons.push(
        <ItemButton
          key={"more"}
          onClick={(e) => removeItemAt(e, path, node)}
          icon={faTrash}
          buttonClass="btn-danger"
        />,
      );
    }

    return buttons;
  }

  const getNodeKey = ({ node }: { node: TreeItem | Item }) => node.id;

  function getActiveNode() {
    const node = getNodeAtPath({
      treeData: treeData,
      path: activePath,
      getNodeKey,
    });
    return node && node.treeIndex > -1 ? node.node : null;
  }

  function changeEntryPoint(
    item: TreeItem,
    checked: boolean,
    changedTreeData?: (TreeItem | Item)[],
  ) {
    const newTreeData = map(changedTreeData || treeData, (data, index) => ({
      ...data,
      isEntryPoint:
        checked && item.id === data.id // set item as entry point if it is checked
          ? true
          : !checked &&
              index ===
                findIndex(
                  changedTreeData || treeData,
                  (data) =>
                    data.type !== "link" &&
                    data.type !== "more" &&
                    data.id !== item.id,
                ) // set first item (not link or more) as entry point if item is unchecked
            ? true
            : false, // reset all others to false (to avoid more than one entry point)
    }));

    setTreeData(newTreeData);
  }

  function submit() {
    updateItems({ body: { items: treeData } });
  }

  const hasNoEntryPointItem =
    findIndex(treeData, { isEntryPoint: true }) === -1;

  const activeNode = getActiveNode();

  return (
    <div>
      <PageTitle
        title={I18n.t("js.administration.app_navigation_items.edit.page_title")}
      />
      <div className="btn-toolbar justify-end mb-4">
        <button
          className="btn btn-success"
          type="submit"
          onClick={submit}
          disabled={hasNoEntryPointItem}
        >
          {I18n.t("js.administration.navigation_items.edit.save")}
        </button>
      </div>
      {isLoading ? (
        <p>
          <FontAwesomeIcon className="mr-2" icon={faSpinner} spin />
          {I18n.t("js.administration.app_navigation_items.loading")}
        </p>
      ) : (
        <div className="row">
          <div className="navigation-manager">
            <div className="navigation-column">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={treeData.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div key={treeKey} className="sortable-tree">
                    {treeData.map((node) => {
                      const path = [node.id];
                      return (
                        <SortableItem
                          key={node.id}
                          node={node}
                          onClick={() => setActivePath(path)}
                          buttons={itemButtons(node, path)}
                          isSelected={isEqual(activePath, path)}
                          className={classNames({
                            "border-red-300 bg-red-50":
                              !node.label && !isSaving,
                          })}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                className="btn btn-primary add-new-item-button"
                disabled={treeData.length === 5 || isSaving}
                onClick={addItem}
              >
                <FontAwesomeIcon className="mr-1" icon={faPlus} />
                {I18n.t("js.administration.navigation_items.edit.add_link")}
              </button>
              {hasNoEntryPointItem && (
                <p className="hint">
                  {I18n.t(
                    "js.administration.app_navigation_items.edit.hint_entry_point_needed",
                  )}
                </p>
              )}
              {treeData.length < 5 && (
                <p className="hint">
                  {I18n.t(
                    "js.administration.app_navigation_items.edit.hint_five_navigation_items_needed",
                  )}
                </p>
              )}
            </div>
            <div className="input-column">
              {activeNode && (
                <ItemForm
                  item={activeNode as any}
                  unusedDefaults={more?.options?.items}
                  changeActiveNode={changeActiveNode}
                  changeEntryPoint={changeEntryPoint}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppNavigationManager;
